import { InteractionRequiredAuthError, PublicClientApplication, type AccountInfo, type IPublicClientApplication } from "@azure/msal-browser";
import type { SpaAuthConfig } from "./authConfig";
import { GRAPH_SCOPES, isAdminConsentError, type GraphScope, type GraphTokenMode, type GraphTokenResult } from "./graph";

// Microsoft Entra ID sign-in with MSAL (ported from Summit Engineering). Redirect sign-in; a token per API call from the
// cache (silent), with an interactive redirect only when Entra says interaction is required. Tokens live in
// sessionStorage (per tab, gone when the tab closes). The API validates every token; nothing here decides access.

/** Where MSAL sends the browser back to: the redirect bridge page (MSAL v5), on this origin and base path. */
export function redirectUri(config: SpaAuthConfig, origin = window.location.origin, base: string = import.meta.env.BASE_URL ?? "/"): string {
  const path = (config.redirectPath ?? "/redirect.html").replace(/^\/+/, "");
  return new URL(path, `${origin}${base.endsWith("/") ? base : `${base}/`}`).toString();
}

export async function createMsalInstance(config: SpaAuthConfig): Promise<IPublicClientApplication> {
  if (!config.configured || !config.clientId || !config.authority) throw new Error("Microsoft sign-in is not configured.");
  const bridge = redirectUri(config);
  const instance = new PublicClientApplication({
    auth: { clientId: config.clientId, authority: config.authority, redirectUri: bridge, postLogoutRedirectUri: bridge },
    cache: { cacheLocation: "sessionStorage" }
  });
  await instance.initialize();
  const result = await instance.handleRedirectPromise();
  if (result?.account) instance.setActiveAccount(result.account);
  else if (!instance.getActiveAccount()) {
    const [first] = instance.getAllAccounts();
    if (first) instance.setActiveAccount(first);
  }
  return instance;
}

export function currentAccount(instance: IPublicClientApplication): AccountInfo | null {
  return instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
}

function scopes(config: SpaAuthConfig): string[] {
  if (!config.apiScope) throw new Error("Microsoft sign-in is not configured.");
  return [config.apiScope];
}

/** Starts the redirect sign-in. `returnTo` is the in-app path to land on afterwards; `chooseAccount` shows Microsoft's account chooser. */
export async function signInRedirect(instance: IPublicClientApplication, config: SpaAuthConfig, returnTo: string, chooseAccount = true): Promise<void> {
  await instance.loginRedirect({
    scopes: scopes(config),
    ...(chooseAccount ? { prompt: "select_account" } : {}),
    redirectStartPage: new URL(returnTo, window.location.origin).toString()
  });
}

/**
 * An access token for the API: from the cache (refreshed silently when needed). When Entra requires interaction
 * (expired session, consent, MFA) the browser is sent to sign in and back; the current request is abandoned.
 */
export async function acquireApiToken(instance: IPublicClientApplication, config: SpaAuthConfig): Promise<string | null> {
  const account = currentAccount(instance);
  if (!account) return null;
  try {
    return (await instance.acquireTokenSilent({ scopes: scopes(config), account })).accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await instance.acquireTokenRedirect({ scopes: scopes(config), account, redirectStartPage: window.location.href });
    }
    throw e;
  }
}

export async function signOutRedirect(instance: IPublicClientApplication): Promise<void> {
  await instance.logoutRedirect({ account: currentAccount(instance) ?? undefined });
}

/**
 * A Microsoft Graph token for one delegated scope (O-R56), separate from the API token. Silent by default and never a
 * redirect at sign-in: when Entra needs consent or interaction, a silent call resolves null. "interactive" (only from a
 * click) opens a pop-up for the same account, and falls back to a redirect when the browser blocks the pop-up.
 */
export async function acquireGraphToken(instance: IPublicClientApplication, scope: GraphScope, mode: GraphTokenMode): Promise<GraphTokenResult> {
  const account = currentAccount(instance);
  if (!account) return { token: null };
  const scopes = [GRAPH_SCOPES[scope]];
  try {
    return { token: (await instance.acquireTokenSilent({ scopes, account })).accessToken };
  } catch (e) {
    if (mode === "silent") return { token: null, consentBlocked: isAdminConsentError(e) };
  }
  try {
    const result = await instance.acquireTokenPopup({ scopes, account, loginHint: account.username });
    if (!result.account || result.account.homeAccountId !== account.homeAccountId) return { token: null };
    return { token: result.accessToken };
  } catch (e) {
    if (isAdminConsentError(e)) return { token: null, consentBlocked: true };
    const code = (e as { errorCode?: string })?.errorCode ?? "";
    if (/popup_window_error|empty_window_error|popup/i.test(code)) {
      await instance.acquireTokenRedirect({ scopes, account, redirectStartPage: window.location.href });
    }
    return { token: null };
  }
}

/** The payload of a JWT, for DISPLAY decisions only — the API validates every token. */
export function decodeJwtClaims(token: string): Record<string, unknown> {
  const part = token.split(".")[1];
  if (!part) return {};
  try {
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return {};
  }
}
